import { useGetContentListQuery } from '@api/api';
import { ContentRequest, EpiBlock, EpiContent } from '@api/types';
import { BlockWrapper, DataLoadingProgress, GenericError } from '@components';
import { isValidElement } from 'react';

/**
 * Content Area Props. Pass either React Node, content from EPI or list of ids.
 * Can use additional block wrapper component
 */
export type ContentAreaBaseProps = {
  content?: EpiContent;
  isCatalogContent?: boolean;
  ids?: number[] | string[];
  children?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  wrapperComponent?: React.FC<any>;
  epiEditMode?: boolean;
  enableContextMenu?: boolean;
  enableNativeOverlay?: boolean;
  /**
   * Use to remove edit buttons in nested blocks
   */
  disableEditPanel?: boolean;
};

export type ContentAreaProps = ContentAreaBaseProps;

/**
 * Map Content Area to Blocks.
 * Display loaded / expanded blocks.
 * Load blocks that are not expanded yet.
 */
export const ContentArea = ({ content, ids, ...rest }: ContentAreaProps) => {
  if (content !== null && isValidElement(content)) {
    return content;
  }
  const shouldLoad = ids || (content?.find((x) => !x.contentLink?.expanded) && !rest.epiEditMode);
  return shouldLoad ? (
    <ContentAreaLoader ids={ids} content={content} {...rest} />
  ) : (
    <ContentAreaDisplay content={content} {...rest} />
  );
};

export type ContentAreaDisplayProps = ContentAreaBaseProps & {
  content?: EpiBlock[];
};

const ContentAreaDisplay = ({ content = [], children, ...props }: ContentAreaDisplayProps) => {
  return (
    <>
      {children}
      {content &&
        content.map &&
        content.map((item: any, index: any) => {
          return <BlockWrapper blockData={item} key={index} {...props} />;
        })}
    </>
  );
};

export type ContentAreaLoaderProps = ContentAreaBaseProps & {
  content?: EpiBlock[];
};

/**
 * Content Area sub component.
 * Load blocks that are not expanded yet.
 */
const ContentAreaLoader = ({
  ids,
  isCatalogContent,
  loadingComponent,
  content = [],
  epiEditMode,
  ...props
}: ContentAreaLoaderProps) => {
  const references = ids
    ? ids
    : (content?.map((x) => x?.contentLink?.id).filter((x) => x !== undefined) as number[]);
  const mappedReferences = isCatalogContent
    ? references.map((x) => x + '__CatalogContent')
    : references;
  const request: ContentRequest = { references: mappedReferences, epiEditMode, expand: '*' };
  const response = useGetContentListQuery(request);
  const { data, isLoading, isSuccess, isError } = response;
  return (
    <>
      {isLoading && (loadingComponent || <DataLoadingProgress />)}
      {isSuccess && data && <ContentAreaDisplay content={data} {...props} />}
      {isError && <GenericError />}
    </>
  );
};
